// src/services/supabaseClient.ts

import { createClient } from '@supabase/supabase-js';
import type { Database, Json } from '../types/supabase';
import type { BranchData, MenuItem as PublishedMenuItem } from '~/types/new_types';
import { normalizeMenuItem } from '~/utils/menuItemHelper';

// Get environment variables for Supabase connection
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if environment variables are defined
if (!supabaseUrl || !supabaseAnonKey) {
	console.error(
		'Supabase URL or Anonymous Key is not defined in environment variables. Make sure to define VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
	);
}

// Helper function to generate IDs (choose one approach)
function generateId(): string {

	return self.crypto.randomUUID(); // Browser API
}

// Function to initialize Supabase with custom storage
export const initializeSupabase = (rememberMe: boolean) => {
	const authStorage = rememberMe ? localStorage : sessionStorage;

	return createClient<Database>(supabaseUrl, supabaseAnonKey, {
		auth: {
			storage: authStorage,
			autoRefreshToken: rememberMe, // Refresh token only if "Remember Me" is enabled
			persistSession: true,
			detectSessionInUrl: true,
		},
	});
};

export const supabase = initializeSupabase(true)

// Export types for the tables as shown in your schema diagram
export type Organization = Database['public']['Tables']['organizations']['Row'];
export type Branch = Database['public']['Tables']['branches']['Row'];
export type MenuItem = Database['public']['Tables']['items']['Row'];
export type Category = Database['public']['Tables']['categories']['Row'];

// Helper functions for common operations

/**
 * Fetches organizations owned by the current user
 */
export const getUserOrganization = async (userId: string) => {
	try {
		const { data, error } = await supabase
			.from('organizations')
			.select('*')
			.eq('owner_id', userId)
			.single()

		if (error) throw error;
		return data;
	} catch (error) {
		console.error('Error fetching branch:', error);
		return null;
	}
};

/**
 * Fetches a branch by ID
 */
// export const getBranchById = async (branchId: string): Promise<Branch | null> => {
// 	try {
// 		const { data, error } = await supabase
// 			.from('branches')
// 			.select('*')
// 			.eq('id', branchId)
// 			.single();

// 		if (error) throw error;
// 		return data;
// 	} catch (error) {
// 		console.error('Error fetching branch:', error);
// 		return null;
// 	}
// };
export const getBranchById = async (branchKey: string): Promise<BranchData | null> => {
	try {
		const { data, error } = await supabase
			.from('published_menus')
			.select('*')
			.eq('branch_key', branchKey)
			.single();

		if (error) throw error;

		// Transform the data to match BranchData structure
		const menuObject = data.menu_object;
		
		if (!menuObject || typeof menuObject !== 'object') {
			throw new Error('Invalid menu object');
		}
		
		const menuData = menuObject as any;
		const rawItems = Array.isArray(menuData.items)
			? (menuData.items as PublishedMenuItem[])
			: [];
		const normalizedItems = rawItems.map((item) => normalizeMenuItem(item));
		const transformedBranchData: BranchData = {
			branch: menuData.branch,
			categories: menuData.categories || [],
			items: normalizedItems,
			summary: menuData.summary,
		};

		return transformedBranchData;
	} catch (error) {
		console.error('Error fetching published menu:', error);
		return null;
	}
};


/**
 * Fetches branches for a specific organization
 */
export const getOrganizationBranches = async (organizationId: string) => {
	return await supabase
		.from('branches')
		.select('*')
		.eq('organization_id', organizationId)
		.order('created_at', { ascending: false });
};

/**
 * Fetches categories for a specific branch
 */
export const getBranchCategories = async (branchId: string) => {
	return await supabase
		.from('categories')
		.select('*')
		.eq('branch_id', branchId)
		.order('created_at', { ascending: false });
};

/**
 * Fetches menu items for a specific branch
 */
export const getBranchMenuItems = async (branchId: string) => {
	return await supabase
		.from('items')
		.select('*')
		.eq('branch_id', branchId)
		.order('created_at', { ascending: false });
};

/**
 * Creates a new organization
 */
export const createOrganization = async (name: string) => {
	const { data: user } = await supabase.auth.getUser();
	if (!user.user) return { data: null, error: new Error('User not authenticated') };

	return await supabase
		.from('organizations')
		.insert([
			{
				name,
				owner_id: user.user.id,
				is_setup: false
			}
		])
		.select()
		.single();
};

export const updateOrganization = async (organizationId: string, organizationData: Partial<Organization>) => {
	return await supabase
		.from('organizations')
		.update({ ...organizationData })
		.eq('id', organizationId)
		.select()
		.single();
};

/**
 * Creates a new branch for an organization
 */
export const createBranch = async (organizationId: string, branchData: Partial<Branch>) => {
	return await supabase
		.from('branches')
		.insert([
			{
				id: generateId(), // Add this line to ensure ID is provided
				organization_id: organizationId,
				created_at: new Date().toISOString(), // Often required by RLS policies
				...branchData
			}
		])
		.select()
		.single();
};

/**
 * Updates a branch
 */
export const updateBranch = async (branchId: string, branchData: Partial<Branch>) => {
	return await supabase
		.from('branches')
		.update(branchData)
		.eq('id', branchId)
		.select()
		.single();
};

/**
 * Creates a category for a branch
 */
export const insertCategory = async (category: any) => {
	return await supabase
		.from('categories')
		.insert([category])
		.select()
		.single();
};

/**
 * Creates a category for a branch
 */
export const updateCategory = async (category: any) => {
	return await supabase
		.from('categories')
		.update(category)
		.eq('id', category.id)
		.select()
		.single();
};

/**
 * Select a menu item by id
 */
export const selectMenuItemById = async (itemId: string) => {
	return await supabase
		.from('items')
		.select('*')
		.eq('id', itemId)
		.single();
};

/**
 * Creates a new menu item for a branch
 */
export const insertMenuItem = async (menuItem: Partial<MenuItem>) => {
	return await supabase
		.from('items')
		.insert([menuItem as MenuItem])
		.select()
		.single();
};

/**
 * Updates a menu item
 */
export const updateMenuItem = async (itemData: Partial<MenuItem>) => {
	return await supabase
		.from('items')
		.update(itemData as MenuItem)
		.eq('id', itemData.id!)
		.select()
		.single();
};

/**
 * Deletes a menu item
 */
export const deleteMenuItem = async (itemId: string) => {
	return await supabase
		.from('items')
		.delete()
		.eq('id', itemId)
		.select()
		.single();
};

/**
 * Get the branch agent
 */
export const getAgentByBranchId = async (branchId: string) => {
	return await supabase
		.from('agent_configuration')
		.select('*')
		.eq('branch_id', branchId)
		.single();
};


export const createUserSession = async (branchId: string) => {

	const branchIdString = String(branchId);
	const result = await supabase.rpc('add_branch_session', { branch_id: branchIdString });
	if (result.data && typeof result.data === 'object' && (result.data as any).data?.session_id) {
		localStorage.setItem('session_id', (result.data as any).data.session_id);
	}
	if (result.error) {
		console.error('Error creating session:', result.error);
	}

	return result;
};

export const insertOrder = async (payload: Json) => {
	return await (supabase as unknown as {
		rpc: (
			fn: string,
			args?: Record<string, unknown>
		) => Promise<{ data: unknown; error: unknown }>;
	}).rpc('insert_order', { payload: payload as Record<string, unknown> });
};

export default supabase;